/**
 * useCreateMealPlan Hook
 *
 * Custom React hook for creating meal plans with form submission handling,
 * loading states, and error management. Integrates with the MealDomain service
 * to create new meal plans and handle navigation after successful creation.
 *
 * @module hooks/useCreateMealPlan
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { apiService } from '@/services/ApiService';
import { MealApiError } from '@/services/domains/MealDomain';
import type { CreateMealPlanRequestFrontend, MealPlanFrontend } from '@/types/mealPlan';
import { getLogger } from '@/services/shared/LoggerFactory';
import { useLoadingProgress } from '@/hooks/useLoadingProgress';
import { calculateEstimatedTimeSeconds } from '@/utils/mealPlanEstimation';

/**
 * Return type for useCreateMealPlan hook
 */
export interface UseCreateMealPlanReturn {
  /** Loading state indicator */
  isSubmitting: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Function to handle form submission */
  handleSubmit: (formData: CreateMealPlanRequestFrontend) => Promise<void>;
  /** Function to clear error state */
  clearError: () => void;
  /** Created meal plan data (available after successful creation) */
  createdMealPlan: MealPlanFrontend | null;
  /** Loading progress state */
  loadingProgress: {
    progress: number;
    loadingText: string;
    isLoading: boolean;
    estimatedTimeSeconds: number;
  };
}

/**
 * Hook for creating meal plans with form submission handling
 *
 * @param onSuccess - Optional callback function to execute after successful creation
 * @param redirectPath - Path to redirect to after successful creation (defaults to '/meal-plans')
 * @returns Object containing submission state, handlers, and created meal plan data
 *
 * @example
 * ```typescript
 * const CreateMealPlanComponent = () => {
 *   const { isSubmitting, error, handleSubmit, clearError } = useCreateMealPlan();
 *
 *   const onFormSubmit = async (formData: CreateMealPlanRequestFrontend) => {
 *     await handleSubmit(formData);
 *   };
 *
 *   return (
 *     <form onSubmit={(e) => {
 *       e.preventDefault();
 *       onFormSubmit(formData);
 *     }}>
 *       {error && <div className="error">{error}</div>}
 *       <button type="submit" disabled={isSubmitting}>
 *         {isSubmitting ? 'Creating...' : 'Create Meal Plan'}
 *       </button>
 *     </form>
 *   );
 * };
 * ```
 */
export const useCreateMealPlan = (
  onSuccess?: (mealPlan: MealPlanFrontend) => void,
  redirectPath: string = '/meal-plans'
): UseCreateMealPlanReturn => {
  const navigate = useNavigate();
  const logger = getLogger();

  // State management
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [createdMealPlan, setCreatedMealPlan] = useState<MealPlanFrontend | null>(null);
  const [estimatedTimeSeconds, setEstimatedTimeSeconds] = useState<number>(30);

  // Loading progress management
  const {
    progress,
    loadingText,
    isLoading,
    startLoading,
    stopLoading,
    completeLoading
  } = useLoadingProgress();

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (formData: CreateMealPlanRequestFrontend) => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Calculate estimated time based on meal plan parameters
      const mealCount = formData.mealCount ?? 1;
      const mealTypesCount = formData.mealTypes?.length || 1;
      const calculatedEstimatedTime = calculateEstimatedTimeSeconds(mealCount, mealTypesCount);
      setEstimatedTimeSeconds(calculatedEstimatedTime);

      // Start the loading progress simulation
      startLoading();

      const updatedFormData = { ...formData };
      // Update meal count to be based on days x meal types selected
      updatedFormData.mealCount = (formData.mealCount ?? 1) * formData.mealTypes.length;
      // Update total budget to be based on servings per meal x budget aud
      updatedFormData.budgetAud = (formData.servingsPerMeal ?? 1) * (formData.budgetAud ?? 5);
      // Add religious restrictions to dietary restrictions for a quick solve on a feature request
      updatedFormData.dietaryRestrictions = formData.dietaryRestrictions?.concat(formData.religiousRestrictions ?? []);

      logger.log('Creating meal plan with data:', updatedFormData);

      // Call the API service to create the meal plan
      const createdMealPlan = await apiService.mealplan.createMealPlan(updatedFormData);

      logger.log('Meal plan created successfully:', {
        id: createdMealPlan.id,
        label: createdMealPlan.label,
        mealCount: createdMealPlan.mealCount,
      });

      // Update state with created meal plan
      setCreatedMealPlan(createdMealPlan);

      // Complete the loading progress (show 100% for 2 seconds)
      await completeLoading();

      // Execute success callback if provided
      if (onSuccess) {
        onSuccess(createdMealPlan);
      }

      // Navigate to the specified path
      navigate(redirectPath, {replace: true});

    } catch (err) {
      logger.error('Failed to create meal plan:', err);

      // Stop loading progress on error
      stopLoading();

      let errorMessage = 'Failed to create meal plan. Please try again.';

      if (err instanceof MealApiError) {
        switch (err.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in to continue.';
            break;
          case 403:
            errorMessage = 'Only dieticians can create meal plans.';
            break;
          case 400:
            if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
              // Create a user-friendly message from field errors
              const fieldErrorMessages = Object.entries(err.fieldErrors)
                .map(([field, message]) => `${field}: ${message}`)
                .join(', ');
              errorMessage = `Invalid meal plan data: ${fieldErrorMessages}`;
            } else {
              errorMessage = 'Invalid meal plan data. Please check your input and try again.';
            }
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || errorMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [navigate, redirectPath, onSuccess, logger, startLoading, completeLoading, stopLoading]);

  return {
    isSubmitting,
    error,
    handleSubmit,
    clearError,
    createdMealPlan,
    loadingProgress: {
      progress,
      loadingText,
      isLoading,
      estimatedTimeSeconds,
    },
  };
};

export default useCreateMealPlan;